import type { CouponUsageState } from '@/schemas/v2/coupon'
import type { PrizeHistoryData } from '@/schemas/v2/reward'

/** 非活性理由。null は「回数制限以外の理由（期間外・スタンプ未取得・消込済み等）」を表す */
export type CouponUnavailableReason = 'limit_reached' | 'used_today' | null

/**
 * クーポンが今この瞬間に消込可能かどうか。
 * 判定は必ずサーバが返す redeemableNow の単一フラグに委ね、個別フィールドから再計算しない。
 * couponUsage が未提供（旧レスポンス / COUPON 以外）の場合は fallback を返し、既存挙動を維持する。
 */
export function isCouponRedeemable(usage: CouponUsageState | null | undefined, fallback = true): boolean {
  return usage == null ? fallback : usage.redeemableNow
}

/**
 * 非活性時の理由を解決する。
 * ONCE は「1回だけ使えるクーポンを使い終えた」状態であり回数制限の話ではないため、
 * 既存の「使用済み」表示に委ねる目的で null を返す。
 * DAILY / UNLIMITED では「上限到達」は恒久的、「本日利用済み」は翌日解消されるため、
 * 両方成立する場合に翌日また使えると誤解させないよう恒久的な理由を優先して返す。
 */
export function resolveCouponUnavailableReason(usage: CouponUsageState | null | undefined): CouponUnavailableReason {
  if (usage == null || usage.redeemableNow) return null
  if (usage.usageLimitType === 'ONCE') return null
  if (usage.maxUseCount != null && usage.usedCount >= usage.maxUseCount) return 'limit_reached'
  if (usage.usedToday) return 'used_today'
  return null
}

/** 抽選ハズレを除いた COUPON エントリを抽出する */
export function pickCouponPrizes(prizes: PrizeHistoryData[] | undefined): PrizeHistoryData[] {
  return (prizes ?? []).filter((p) => !p.isLotteryLost && p.prize?.type === 'COUPON')
}

/** 抽選ハズレを除いた COUPON 以外の賞品エントリが存在するか（混在特典の判定用） */
export function hasNonCouponPrize(prizes: PrizeHistoryData[] | undefined): boolean {
  return (prizes ?? []).some((p) => !p.isLotteryLost && p.prize?.type !== 'COUPON')
}

/**
 * EXCHANGED でも再消込可能な COUPON エントリ（DAILY / UNLIMITED）が存在するか。
 * 旧仕様で消込済みのデータを救済するため、couponUsage を明示的に返すレスポンスに限定して判定する。
 * couponSpecificSpots を渡すとスポット指定 / 非指定のどちらかに対象を絞る（未指定は両方が対象）。
 * ボタン活性（RewardActionButton）とステートマシンのガードで同じ判定を共有するために本関数へ集約している。
 */
export function hasReusableExchangedCoupon(
  prizes: PrizeHistoryData[] | undefined,
  couponSpecificSpots?: boolean,
): boolean {
  return pickCouponPrizes(prizes).some(
    (p) =>
      p.prize?.couponUsage?.redeemableNow === true &&
      (couponSpecificSpots === undefined || Boolean(p.prize?.couponSpecificSpots) === couponSpecificSpots),
  )
}

/**
 * 非活性理由に対応する特典クーポンのラベルを返す。
 * 特典詳細のメインボタンと獲得賞品一覧ダイアログの行ボタンで共通に使用する。
 */
export function getCouponUnavailableLabel(
  t: (key: string, defaultValue: string) => string,
  reason: CouponUnavailableReason,
): string {
  if (reason === 'limit_reached') return t('reward_detail.coupon_limit_reached', '利用回数の上限に達しました')
  if (reason === 'used_today') return t('reward_detail.coupon_used_today', '本日は利用済みです')
  return t('reward_detail.coupon_unavailable', '現在ご利用いただけません')
}
